import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CircleAlert, CircleCheck, Clock4 } from "lucide-react";
import React from "react";

export enum WorklistStatus {
    IN_PROGRESS = 1,
    PENDING = 2,
    COMPLETED = 3
}

export enum Priority {
    HIGH_PRIORITY = 1,
    NON_HIGH_PRIORITY = 2
}

export interface WorklistItemData {
    status: WorklistStatus;
    title: string;
    subtitle: string;
    priority: Priority;
    units: number;
}

interface WorklistItemProps extends WorklistItemData {}

const WorklistItemCard: React.FC<WorklistItemProps> = ({
    status,
    title,
    subtitle,
    priority,
    units
}) => {
    const getStatusIcon = () => {
        switch (status) {
            case WorklistStatus.PENDING:
                return <CircleAlert className="text-orange-500" />;
            case WorklistStatus.IN_PROGRESS:
                return <Clock4 className="text-blue-500" />;
            case WorklistStatus.COMPLETED:
                return <CircleCheck className="text-green-500" />;
            default:
                return <CircleAlert className="text-orange-500" />;
        }
    };

    const getStatusBadgeProps = () => {
        switch (status) {
            case WorklistStatus.PENDING:
                return {
                    className: "bg-red-600 text-white font-normal",
                    text: "Pending"
                };
            case WorklistStatus.IN_PROGRESS:
                return {
                    className: "bg-blue-200 text-blue-700 font-normal",
                    text: "In Progress"
                };
            case WorklistStatus.COMPLETED:
                return {
                    className: "bg-green-200 text-green-700 font-normal",
                    text: "Completed"
                };
            default:
                return {
                    className: "bg-red-600 text-white font-normal",
                    text: "Pending"
                };
        }
    };

    const statusBadgeProps = getStatusBadgeProps();

    return (
        <Card className="bg-[#FFFFFF] relative border rounded-2xl w-full">
            <CardContent className="w-full p-4 flex flex-row items-center justify-between gap-y-2">
                <div className="flex flex-row items-center justify-start gap-x-4">
                    {getStatusIcon()}
                    <div className="flex flex-col items-start justify-center gap-y-1">
                        <span className="font-medium">{title}</span>
                        <span className="font-normal text-gray-500">Batch: {subtitle}</span>
                    </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-4">
                    <div className="flex flex-col items-end justify-center gap-y-1">
                        <span className="text-sm font-normal mr-2">{units} units</span>
                        {priority === Priority.HIGH_PRIORITY && (
                            <Badge className="bg-white text-black font-normal border border-gray-300">
                                High Priority
                            </Badge>
                        )}
                    </div>
                    <Badge className={statusBadgeProps.className}>
                        {statusBadgeProps.text}
                    </Badge>
                    <Button variant="outline" className="px-4 h-[36px] rounded-[12px]">
                        View
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default WorklistItemCard;
